import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { YStack } from "tamagui";

import AuthScaffold, { AuthDivider, AuthFooterText, AuthInfoPanel } from "@/components/AuthScaffold";
import { supabase } from "@/integrations/supabase/client";
import { getRememberSessionPreference, setRememberSessionPreference } from "@/lib/authPersistence";
import { trackKindredProductEvent } from "@/lib/productAnalytics";
import { beginWebSocialAuth } from "@/lib/socialAuth";
import {
  buildWebAuthPath,
  consumeWebPostAuthRedirect,
  getWebReturnToFromSearch,
  rememberWebPostAuthRedirect,
} from "@/lib/postAuthRedirect";
import { toast } from "sonner";
import { AppButton, AppCheckbox, AppFieldGroup, AppInput, AppText } from "../../ui-foundation";
import {
  EMAIL_CONFIRMATION_SIGNIN_MESSAGE,
  isEmailConfirmationRequiredError,
} from "../../shared/auth/emailConfirmation";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
    <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
    <path d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" fill="#FBBC05"/>
    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
  </svg>
);

const AppleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
    <path d="M14.26 9.55c-.02-2.29 1.87-3.4 1.96-3.45-1.07-1.57-2.74-1.78-3.33-1.81-1.42-.14-2.77.84-3.49.84-.72 0-1.83-.82-3.01-.8-1.54.02-2.97.9-3.76 2.28-1.6 2.78-.41 6.9 1.15 9.16.77 1.11 1.68 2.35 2.88 2.31 1.16-.05 1.59-.75 2.99-.75 1.4 0 1.79.75 3.01.72 1.24-.02 2.03-1.13 2.79-2.24.88-1.28 1.24-2.52 1.26-2.58-.03-.01-2.43-.93-2.45-3.68zM11.98 2.9c.64-.78 1.07-1.86.95-2.94-.92.04-2.03.61-2.69 1.38-.59.68-1.11 1.77-.97 2.82 1.02.08 2.06-.52 2.71-1.26z" fill="currentColor"/>
  </svg>
);

const SignIn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = getWebReturnToFromSearch(location.search);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [keepLoggedIn, setKeepLoggedIn] = useState(getRememberSessionPreference());
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (returnTo) {
      rememberWebPostAuthRedirect(returnTo);
    }
  }, [returnTo]);

  const validate = () => {
    const nextErrors: Record<string, string> = {};
    if (!email.trim()) nextErrors.email = "Please enter your email";
    if (!password) nextErrors.password = "Please enter your password";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);

    try {
      await beginWebSocialAuth({
        provider: "google",
        intent: "signin",
        rememberSession: keepLoggedIn,
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Google sign-in failed");
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setAppleLoading(true);

    try {
      await beginWebSocialAuth({
        provider: "apple",
        intent: "signin",
        rememberSession: keepLoggedIn,
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Apple sign-in failed");
    } finally {
      setAppleLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    await setRememberSessionPreference(keepLoggedIn);
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);

    if (error) {
      if (isEmailConfirmationRequiredError(error)) {
        toast.error(EMAIL_CONFIRMATION_SIGNIN_MESSAGE);
        return;
      }

      toast.error(error.message === "Invalid login credentials" ? "Invalid email or password. Please try again." : error.message);
      return;
    }

    await trackKindredProductEvent({
      userId: data.user?.id,
      eventName: "sign_in_success",
      screenName: "sign_in",
      metadata: { method: "password" },
    });
    navigate(consumeWebPostAuthRedirect(), { replace: true });
  };

  return (
    <AuthScaffold
      eyebrow="Welcome back"
      title="Sign in to your community."
      description="Pick up where you left off, whether that was a conversation, an adventure, or a quiet browse with a cup of tea."
    >
      <YStack tag="form" onSubmit={handleSignIn} gap="$sm" autoComplete="on">
        <AppFieldGroup label={<label htmlFor="email">Email</label>} error={errors.email}>
          <AppInput
            id="email"
            name="email"
            type="email"
            value={email}
            onChangeText={setEmail}
            placeholder="you@example.com"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect={false}
            spellCheck={false}
            inputMode="email"
          />
        </AppFieldGroup>

        <AppFieldGroup
          label={
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <label htmlFor="password">Password</label>
              <Link to="/reset-password" className="kindred-inline-link">
                Forgot password?
              </Link>
            </div>
          }
          error={errors.password}
        >
          <AppInput
            id="password"
            name="password"
            type="password"
            value={password}
            onChangeText={setPassword}
            placeholder="Your password"
            autoComplete="current-password"
            autoCapitalize="none"
            autoCorrect={false}
            spellCheck={false}
          />
        </AppFieldGroup>

        <AuthInfoPanel>
          <AppCheckbox
            id="keepLoggedIn"
            checked={keepLoggedIn}
            onCheckedChange={setKeepLoggedIn}
            label={
              <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                Keep me logged in on this device.
              </AppText>
            }
          />
        </AuthInfoPanel>

        <AppButton type="submit" disabled={loading} width="100%">
          {loading ? "Signing in..." : "Sign in"}
        </AppButton>

        <AuthDivider />

        <YStack gap="$xs">
          <AppButton
            type="button"
            tone="outline"
            onPress={handleGoogleSignIn}
            disabled={googleLoading || appleLoading}
            width="100%"
          >
            <GoogleIcon />
            {googleLoading ? "Starting Google..." : "Continue with Google"}
          </AppButton>
          <AppButton
            type="button"
            tone="outline"
            onPress={handleAppleSignIn}
            disabled={googleLoading || appleLoading}
            width="100%"
          >
            <AppleIcon />
            {appleLoading ? "Starting Apple..." : "Continue with Apple"}
          </AppButton>
        </YStack>

        <AppText textAlign="center" fontSize="$xs" lineHeight="$sm" color="$colorMuted">
          If you have turned on two-step verification, Kindred will ask for your authenticator code next.
        </AppText>
      </YStack>

      <AuthFooterText>
        New to Kindred?{" "}
        <Link to={buildWebAuthPath("/signup", returnTo)} className="kindred-inline-link">
          Join free
        </Link>
      </AuthFooterText>

      <AuthFooterText>
        Trouble signing in?{" "}
        <Link
          to={`/login-support${email.trim() ? `?email=${encodeURIComponent(email.trim())}` : ""}`}
          className="kindred-inline-link"
        >
          Get support
        </Link>
      </AuthFooterText>

      <AuthFooterText>
        <Link to="/terms" className="kindred-inline-link">
          Terms &amp; Conditions
        </Link>{" "}
        ·{" "}
        <Link to="/privacy" className="kindred-inline-link">
          Privacy Policy
        </Link>
      </AuthFooterText>
    </AuthScaffold>
  );
};

export default SignIn;
